import axios, { AxiosResponse } from "axios";

import type {
    NewsResponse,
    EditNewsPayload,
    NewsItem,
    CreateNewsPayload,
} from "./types/NewsResponce";

const BASE =
    "/services/isnaportal/open-api/news";

const SECURED =
    "/services/isnaportal/api/news";

/**
 * PUBLIC
 * Список новостей.
 */
export const getNews = (
    _token?: string,
    params: Record<string, any> = {}
): Promise<AxiosResponse<NewsResponse>> => {
    return axios.get(BASE, {
        params: {
            locale: "RU",
            size: 10,
            ...params,
        },
    });
};

/**
 * PUBLIC
 * Получить одну новость.
 *
 * Пока административный GET /api/news/{id}
 * со всеми переводами не подтверждён,
 * edit-форма получает RU и KK отдельно
 * через public endpoint.
 */
export const getNewsById = (
    _token: string,
    newsId: number,
    lang: string
): Promise<AxiosResponse<NewsItem>> => {
    return axios.get(
        `${BASE}/${newsId}`,
        {
            params: {
                locale: lang,
            },
        }
    );
};

/**
 * ADMIN
 * Список новостей для административной части.
 */
export const getAdminNews = (
    _token: string,
    params: Record<string, any> = {}
): Promise<AxiosResponse<NewsResponse>> => {
    return axios.get(
        BASE,
        {
            params,
        }
    );
};
/**
 * ADMIN
 * Создание новости.
 */
export const createNews = (
    token: string,
    payload: CreateNewsPayload
): Promise<AxiosResponse<NewsItem>> => {
    return axios.post(
        SECURED,
        payload,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`,
                "Content-Type":
                    "application/json",
                Accept:
                    "application/json",
            },
        }
    );
};

/**
 * ADMIN
 * Обновление новости.
 */
export const updateNews = (
    token: string,
    id: number,
    payload: EditNewsPayload
): Promise<AxiosResponse<NewsItem>> => {
    return axios.put(
        `${SECURED}/${id}`,
        payload,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`,
                "Content-Type":
                    "application/json",
                Accept:
                    "application/json",
            },
        }
    );
};

/**
 * ADMIN
 * Удаление новости.
 */
export const deleteNews = (
    token: string,
    id: number
): Promise<
    AxiosResponse<void | NewsItem>
> => {
    return axios.delete(
        `${SECURED}/${id}`,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`,
            },
        }
    );
};

/**
 * ADMIN
 * Удаление прикреплённых файлов.
 */
export const deleteNewsFiles = (
    token: string,
    newsId: number,
    fileUidList: string[]
) => {
    return axios.delete(
        `${SECURED}/${newsId}/files`,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`,
                "Content-Type":
                    "application/json",
            },
            data: fileUidList,
        }
    );
};

/**
 * ADMIN
 * Добавление файлов к уже созданной новости.
 *
 * Используется как для изображений,
 * так и для обычных вложений.
 */
export const attachFilesToNews = (
    token: string,
    newsId: number,
    files: File[]
) => {
    const formData =
        new FormData();

    files.forEach((file) => {
        formData.append(
            "files",
            file
        );
    });

    return axios.post(
        `${SECURED}/${newsId}/files`,
        formData,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`,
            },
        }
    );
};

/**
 * Старый multipart-вариант.
 * Пока оставляем для обратной совместимости,
 * даже если текущая форма его не использует.
 */
export const updateNewsMultipart = (
    token: string,
    id: number,
    payload: any,
    files: File[]
) => {
    const formData =
        new FormData();

    formData.append(
        "request",
        JSON.stringify(payload)
    );

    files.forEach((file) => {
        formData.append(
            "files",
            file
        );
    });

    return axios.put(
        `${SECURED}/${id}/multipart`,
        formData,
        {
            headers: {
                Authorization:
                    `Bearer ${token}`,
                Accept:
                    "application/json",
            },
        }
    );
};